import { Badge } from "@/components/Badge";
import i18n from "@/i18n";
import { DownloadButton } from "@/components/DownloadButton";
import { ExportStatus } from "@/lib/utils/exportStatus";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { parseDateWithHours } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/react";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";
import { itemSchema } from "./edit_card";

export type N1DataUpload = {
  id: string;
  generated_at: string;
  client: string;
  report_date: string;
  user: string;
  file_name: string;
  status: string;
};

const t = (key: string) =>
  i18n.t(`${ClientTables.N1_DATA_UPLOAD}.columns.${key}`, {
    ns: "tables",
  });

export const data_upload_columns: ColumnDef<N1DataUpload>[] = [
  { accessorKey: itemSchema.id, header: () => t(itemSchema.id) },
  {
    accessorKey: itemSchema.file_name,
    header: () => t(itemSchema.file_name),
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <span className="whitespace-nowrap overflow-hidden text-ellipsis block">
          {value}
        </span>
      );
    },
  },
  { accessorKey: itemSchema.user_name, header: () => t(itemSchema.user_name) },
  {
    accessorKey: itemSchema.status,
    header: () => t(itemSchema.status),
    cell: ({ getValue }) => {
      const value = getValue() as ExportStatus;
      const badgeColor = statusToBadgeColor[value] || "error";
      return <Badge color={badgeColor} />;
    },
  },
  {
    accessorKey: itemSchema.provider_config_name,
    header: () => t(itemSchema.provider_config_name),
  },
  {
    accessorKey: itemSchema.created_at,
    header: () => t(itemSchema.created_at),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue("created_at");
      if (cell_data) {
        return parseDateWithHours(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: itemSchema.updated_at,
    header: () => t(itemSchema.updated_at),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue("updated_at");
      if (cell_data) {
        return parseDateWithHours(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: "download",
    header: () =>
      i18n.t("actions.download", {
        ns: "common",
      }),
    cell: ({ row }) => {
      const id = row.getValue("id") as string;
      const fileName = row.getValue("file_name") as string;
      const href = `${
        import.meta.env.VITE_BACKEND_URL
      }/${Clients.N1}/${ClientTables.N1_DATA_UPLOAD}/download/${id}`;

      return (
        <div className="flex flex-row items-center justify-between w-full">
          <DownloadButton
            href={href}
            filename={`${fileName}`}
            size="sm"
            variant="light"
            color="primary"
            isIconOnly
          />
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {i18n.t("actions.details")}
            </Link>
          </div>
        </div>
      );
    },
  },
];
